import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useGo } from "./Nav";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const go = useGo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const monoY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const monoRotate = useTransform(scrollYProgress, [0, 1], [0, 8]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] bg-oxblood overflow-hidden flex flex-col"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 55% at 30% 40%, rgba(221,194,140,0.10), transparent 65%)" }}
      />

      <motion.div
        style={{ y: monoY, rotate: monoRotate, opacity: fade }}
        className="absolute -right-[6vw] top-[8vh] font-display text-outline-ivory select-none pointer-events-none text-[52vw] md:text-[38vw] leading-none"
        aria-hidden
      >
        JF
      </motion.div>

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1600px] w-full mx-auto px-6 md:px-12 pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-brass-soft text-xs md:text-sm tracking-[0.45em] uppercase mb-10"
        >
          Est. 2013 — Jaipur, Rajasthan
        </motion.p>

        <h1 className="font-display text-ivory text-[15vw] md:text-[9.5vw] leading-[0.95] tracking-tight">
          <Line delay={0.35}>Furniture</Line>
          <Line delay={0.5}>
            <span className="font-light">that tells</span>
          </Line>
          <Line delay={0.65}>
            <em className="text-brass-soft font-light">your story.</em>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl text-ivory/70 text-base md:text-lg font-light leading-relaxed"
        >
          Handcrafted solid wood furniture from our own Jaipur factory — shop the collection,
          book online, and have it delivered to your home, villa, café or hotel.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap gap-5"
        >
          <button
            data-testid="hero-shop-btn"
            onClick={() => go("shop")}
            className="bg-ivory text-oxblood-ink hover:bg-brass-soft px-9 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-400"
          >
            Shop the Collection
          </button>
          <button
            data-testid="hero-book-btn"
            onClick={() => go("book")}
            className="border border-ivory/40 text-ivory hover:border-brass hover:text-brass-soft px-9 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-400"
          >
            Book Your Furniture
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 pb-8 flex justify-center"
      >
        <div className="flex flex-col items-center gap-3 text-ivory/50">
          <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="block w-px h-10 bg-gradient-to-b from-brass to-transparent"
          />
        </div>
      </motion.div>
    </section>
  );
}
